/**
 * chatExport.js — CSV / clipboard helpers for the AI assistant widget.
 *
 * The assistant answers in markdown; ChatWidget.jsx parses that into blocks
 * (see parseBlocks there) and renders them. These helpers turn the *table*
 * blocks into clean, Excel-friendly CSV and handle the browser plumbing
 * (Blob → object URL → temporary <a download>) with no server round-trip and
 * no new dependencies.
 *
 * The cell rule and the download itself are src/utils/exportCsv.js's
 * (utils/csv.js csvCell, exportCsv.js downloadBlob); exportCsv there is for an array of objects and has
 * no markdown stripping, numeric normalisation or multi-table output.
 */
import { csvCell } from '../../utils/csv';
import { downloadBlob } from '../../utils/exportCsv';
import { localDay } from '../../utils/localDay';

const EOL = '\r\n'; // RFC 4180 line ending — safest for Excel
const BOM = '\uFEFF'; // makes Excel read the file as UTF-8

/* ── text cleanup ─────────────────────────────────────────────────────────── */

/** Drop markdown emphasis so a cell carries data, not formatting. */
export function stripMarkdown(value) {
  return String(value == null ? '' : value)
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1') // [label](url) → label
    .replace(/`([^`]*)`/g, '$1') // `code`
    .replace(/\*\*([^*]+)\*\*/g, '$1') // **bold**
    .replace(/__([^_]+)__/g, '$1') // __bold__
    .replace(/\*([^*]+)\*/g, '$1') // *em*
    .replace(/\s+/g, ' ')
    .trim();
}

// Matches a whole cell that is a formatted money/number: it must either carry
// thousands separators ("1,234", "$1,234.56") or a currency symbol ("$1200").
// Deliberately does NOT match "43%", "1,234 hrs", "$1.2M", "2026-07-28".
const MONEY_NUMBER = /^\(?\s*-?\s*[$€£]?\s*-?\d{1,3}(?:,\d{3})+(?:\.\d+)?\s*\)?$|^\(?\s*-?\s*[$€£]\s*-?\d+(?:\.\d+)?\s*\)?$/;

/**
 * "$1,234.56" → "1234.56", "1,234" → "1234", "($2,000)" → "-2000".
 * Anything that is not wholly a formatted number is returned untouched
 * (percentages, dates, "$1.2M", plain text …).
 */
export function normalizeNumeric(value) {
  const raw = String(value == null ? '' : value).trim();
  if (!MONEY_NUMBER.test(raw)) return raw;
  const negative = /^\(.*\)$/.test(raw) || raw.includes('-');
  const digits = raw.replace(/[(),\s$€£-]/g, '');
  if (!/^\d+(?:\.\d+)?$/.test(digits)) return raw;
  return (negative ? '-' : '') + digits;
}

/** Cell pipeline: markdown out, numbers normalised. */
export function cellText(value) {
  return normalizeNumeric(stripMarkdown(value));
}

/* ── CSV serialisation ────────────────────────────────────────────────────── */

/** One table → CSV text (header row + body), no trailing newline. */
export function tableToCsv(table) {
  if (!table) return '';
  const headers = (table.headers || []).map(cellText);
  const width = headers.length;
  const lines = [headers.map(csvCell).join(',')];
  (table.rows || []).forEach((row) => {
    const cells = [];
    for (let i = 0; i < width; i++) cells.push(csvCell(cellText(row[i])));
    lines.push(cells.join(','));
  });
  return lines.join(EOL);
}

/** Many tables → one CSV file: each block keeps its own header row, blank line between. */
export function tablesToCsv(tables) {
  return (tables || [])
    .map(tableToCsv)
    .filter(Boolean)
    .join(EOL + EOL);
}

/* ── naming ───────────────────────────────────────────────────────────────── */

export function slugify(text, fallback = 'table') {
  const slug = stripMarkdown(text)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48)
    .replace(/-+$/g, '');
  return slug || fallback;
}

/** Local calendar date as YYYY-MM-DD. */
export function todayStamp(date = new Date()) {
  return localDay(date);
}

/** globalops-<slug>-<YYYY-MM-DD>.csv */
export function csvFilename(label, fallback = 'table') {
  return `globalops-${slugify(label, fallback)}-${todayStamp()}.csv`;
}

/* ── block helpers (operate on ChatWidget's parseBlocks output) ────────────── */

const LABEL_PARAGRAPH = /^\*\*[^*]{1,60}\*\*:?$/;

/**
 * Pull every table block out, tagging each with the nearest preceding
 * heading (or short all-bold paragraph, which the assistant uses as a label).
 * Returns [{ index, label, headers, rows }].
 */
export function collectTables(blocks) {
  const tables = [];
  let label = '';
  (blocks || []).forEach((b, index) => {
    if (!b) return;
    if (b.type === 'heading') label = stripMarkdown(b.text);
    else if (b.type === 'p' && LABEL_PARAGRAPH.test(String(b.text).trim())) label = stripMarkdown(b.text);
    else if (b.type === 'table') tables.push({ index, label, headers: b.headers, rows: b.rows });
  });
  return tables;
}

/** Blocks → readable plain text (for the Copy action). */
export function blocksToPlainText(blocks) {
  const parts = [];
  (blocks || []).forEach((b) => {
    if (!b) return;
    if (b.type === 'heading' || b.type === 'p') parts.push(stripMarkdown(b.text));
    else if (b.type === 'hr') parts.push('---');
    else if (b.type === 'ul') parts.push(b.items.map((it) => `- ${stripMarkdown(it)}`).join('\n'));
    else if (b.type === 'ol') parts.push(b.items.map((it, i) => `${i + 1}. ${stripMarkdown(it)}`).join('\n'));
    else if (b.type === 'table') {
      const rows = [(b.headers || []).map(stripMarkdown)].concat((b.rows || []).map((r) => (r || []).map(stripMarkdown)));
      parts.push(rows.map((r) => r.join(' | ')).join('\n'));
    }
  });
  return parts.join('\n\n');
}

/* ── browser plumbing ─────────────────────────────────────────────────────── */

/** Download CSV text as a file — Blob → object URL → temp <a download> → revoke. */
export function downloadCsvText(filename, csv) {
  if (!csv) return false;
  downloadBlob(new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' }), filename);
  return true;
}

/** Clipboard write with a select-and-prompt fallback. Resolves true on success. */
export async function copyText(text) {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to the legacy path */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '-1000px';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, ta.value.length);
    const ok = typeof document.execCommand === 'function' && document.execCommand('copy');
    document.body.removeChild(ta);
    if (ok) return true;
  } catch {
    /* fall through to the prompt */
  }
  try {
    window.prompt('Copy this text with Ctrl+C, then press Enter', text);
    return true;
  } catch {
    return false;
  }
}
